import { Box, IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectFeatureFlagAgentCart,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectIsCarPresent } from '../../selectors/message';
import useAISuggestionActions from '../AISuggestions/useAISuggestionActions.hooks';
import useEventListener from '../../hooks/useEventListener.hooks';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { AI_INCORRECT_RESPONSE_ISSUE_TYPE } from '../../constants/issueTypes';
import {
  ISSUE_FLAG_BADGE_CLASS,
  ISSUE_FLAG_BADGE_ICON_STYLE,
  ISSUE_FLAG_BADGE_REVEAL_SX,
  ISSUE_FLAG_BADGE_SX,
} from '../IssueReport/issueFlagBadgeStyle';

/**
 * PULSE ADVISE (HITL 3.0) — the AI co-pilot's recommended ask, folded into the
 * Pulse strip from the old Action Center: the suggestion text with Accept /
 * Dismiss (tick / cross) on one row.
 *
 * Gated by the agent_cart flag (same master switch as the standalone suggestion
 * surface). Accept / Dismiss and the Alt+A / Alt+D shortcuts drive the shared
 * useAISuggestionActions path. Deliberately omits the old "AI recommends you
 * ask:" label and the reason line; the slash-command reminder now lives in
 * PulseSignals. Renders nothing when there's no active suggestion.
 */
// ── HITL 7.0 inline suggestion (HITL-1668) ───────────────────────────────────
// The ask leaves the Copilot strip and renders as the last child of the
// transcript, where it reads as the next thing that could be said rather than
// as a notice parked beside the conversation it is answering.
//
// Inline styles, not new makeStyles rules: this repo's JSS class names come
// from one global counter, so a new rule renumbers class names in unrelated
// snapshot suites. The 3.0 rules below are untouched.

// Grey, NOT a brand hue: the box is not a third participant in the conversation,
// it is a control the console is offering. Amber was the original runner-up (it
// already means "the AI put this here, you confirm") but it reads as a fourth
// voice. A navy fill was tried and rejected too — at full strength behind the
// text it overpowered the transcript around it.
const HITL7_FILL = Colors.porcelain;

// The header's own colour for the text and the badge, so the box belongs to the
// same console as the header and the Order Flow pills rather than carrying a
// blue of its own. 14:1 against the grey fill, and 15.6:1 for the white badge
// glyph sitting on it.
//
// NOTE for whoever revisits this: the original design paired this grey fill with
// a blue arrival flash, on the explicit reasoning that grey costs arrival
// salience and the flash bought it back. The flash has since been removed by
// decision — it read as a background changing on its own while the agent was
// reading. So the grey now stands WITHOUT the compensation it was granted on
// condition of. If suggestions turn out to be missed in the field, that pairing
// is the first thing to re-examine.
const HITL7_COPILOT_COLOR = Colors.daintree;

// 16px, and that number is load-bearing. The badge is centred ON the left
// border, so it overhangs by half its width — 8px — and `overflowX: hidden`
// on the transcript clips at the PADDING box (the same mechanism that keeps the
// 3.0 bubbles' tails visible). So the room to the left of this box has to be at
// least 8px or the badge loses its outer edge.
//
// It used to come entirely from the transcript's 8px inset. HITL-1673 cut that
// inset to 4px for line width, which left the badge 4px short — hence the 6px
// `marginLeft` on the box below. Whoever changes either number: the sum of the
// transcript's horizontal padding and that margin must stay >= 8.
const HITL7_BADGE_PX = 16;

const HITL7_BOX_STYLE: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  // Vertically centred against the text, however many lines it wraps to.
  alignItems: 'center',
  gap: '8px',
  // No top margin: MessageContainer already ends in a 10px spacer, so one here
  // stacked on top of it and read as a gap twice the size of any other in the
  // pane.
  // Left inset clears the half of the badge that sits inside the box.
  padding: '6px 6px 6px 14px',
  // Room for the OUTER half of the badge. See HITL7_BADGE_PX above: 4px of
  // transcript padding plus this 6px is 10px, comfortably past the 8px the
  // overhang needs. Left only — a top margin would stack on the 10px spacer
  // MessageContainer already ends in.
  marginLeft: '6px',
  backgroundColor: HITL7_FILL,
  // Dashed, so the box reads as provisional — something offered, not something
  // said. Every other box in the transcript is solid or has no border at all.
  border: `1px dashed ${Colors.botticelli}`,
  borderRadius: '8px',
};

const HITL7_BADGE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: `-${HITL7_BADGE_PX / 2}px`,
  top: '50%',
  transform: 'translateY(-50%)',
  width: `${HITL7_BADGE_PX}px`,
  height: `${HITL7_BADGE_PX}px`,
  borderRadius: '50%',
  backgroundColor: HITL7_COPILOT_COLOR,
  color: Colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const HITL7_BADGE_ICON_STYLE: React.CSSProperties = { fontSize: '10px' };

const HITL7_TEXT_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  fontSize: HITL3_FONT_SIZE,
  fontWeight: 500,
  color: HITL7_COPILOT_COLOR,
  // Wraps to as many lines as it needs. Explicitly never truncated: a clipped
  // suggestion is worse than none, because the agent cannot tell what they
  // would be saying before they accept it.
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

// Solid green tick / red cross, applied through `sx` rather than the makeStyles
// classes the 3.0 box uses. That is not a style preference: MUI's own IconButton
// emotion styles outrank makeStyles for `background-color`, so the classes'
// fills were losing and the buttons rendered as a bare tick and a bare cross —
// the dismiss one a light grey glyph on a grey box, effectively invisible.
// `sx` is emitted as emotion, so it wins on the same terms.
//
// The fills are the order buttons' own: `abbey` is the Finish Order background
// and `torchRed` is what `color="error"` resolves to on Cancel Order. Accepting
// and dismissing a suggestion therefore carry the same yes/no colours the agent
// already acts on at the other end of the console.
//
// The GLYPHS differ, because the two fills are not equally dark. White on the
// red is 3.9:1 and clears the 3:1 non-text minimum. White on the green is only
// 1.87:1 — that bright yellow-green is very light — which would put a barely
// visible tick back on the button, the exact problem this change is fixing. The
// tick is therefore near-black, at 8.3:1, the same dark-on-light treatment the
// amber Conversation intent pills already use.
// HITL-1673: the tick and the cross are the badge's twins, so they take its
// proportions exactly — a 16px filled circle around a 10px glyph. Sizing only
// the GLYPH to 16px was the wrong half: the painted circle is the button, and
// at MUI's `p: 0.5` that made it 24px, which is what read as oversized.
//
// The badge's own icon style, reused rather than restated, so the three marks
// cannot drift apart.
//
// An INLINE style, not `sx`: `fontSize="small"` puts MUI's own 1.25rem class on
// the icon, and beating it from the button needs a descendant selector whose
// win depends on emotion's injection order. Inline needs no such argument.
const HITL7_MARK_STYLE = HITL7_BADGE_ICON_STYLE;

// Painted circle 3 + 10 + 3 = 16px, the badge's size.
//
// A 4px transparent border was tried here to hold the hit area at 24px while
// the circle painted at 16. It was removed: an invisible 4px box on each button
// stacks with the row gap, so two circles 4px apart LOOKED 12px apart, and
// closing that gap back up would have overlapped the two hit areas — with one
// button accepting and the other dismissing, an edge click landing on the wrong
// one is a worse bug than a small target.
const HITL7_MARK_BUTTON_SX = {
  padding: '3px',
} as const;

const HITL7_ACCEPT_SX = {
  ...HITL7_MARK_BUTTON_SX,
  backgroundColor: Colors.abbey,
  color: Colors.primaryButtonTextColor,
  '&:hover': { backgroundColor: Colors.abbey },
} as const;

const HITL7_DISMISS_SX = {
  ...HITL7_MARK_BUTTON_SX,
  backgroundColor: Colors.torchRed,
  color: Colors.white,
  '&:hover': { backgroundColor: Colors.torchRed },
} as const;

const HITL7_ACTIONS_STYLE: React.CSSProperties = {
  display: 'flex',
  // 2px. The two marks are one control pair, so they sit closer to each other
  // than to the text they follow, which the box's own 8px gap already spaces.
  gap: '2px',
  flexShrink: 0,
  alignSelf: 'center',
};

const useStyles = makeStyles((theme: Theme) => ({
  ask: {
    // 8px inset on all sides; the bottom margin also keeps the ask off the
    // pinned AI status band at the bottom of the Pulse card.
    marginTop: HITL3_PADDING,
    marginBottom: HITL3_PADDING,
    marginLeft: HITL3_PADDING,
    marginRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: '#eef1fb',
    border: '1px solid #c5cae9',
    borderRadius: '8px',
    padding: theme.spacing(0.875, 1.125),
  },
  // Match the "Welcome" intent pill's text: medium weight, shared hitl3 size.
  q: {
    flex: 1,
    minWidth: 0,
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    color: Colors.readBack,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  okBtn: {
    backgroundColor: Colors.green,
    color: Colors.white,
    '&:hover': { backgroundColor: Colors.green },
  },
  noBtn: {
    backgroundColor: Colors.white,
    color: '#79808a',
    border: `1px solid ${Colors.alto}`,
  },
}));

const PulseAdvise = () => {
  const classes = useStyles();

  // The co-pilot advise loop is gated by the agent_cart flag, matching the
  // standalone AISuggestions surface.
  const isAgentCartEnabled = useSelector(selectFeatureFlagAgentCart);
  // HITL-1663: the 7.0 Pulse strip is one wrapping flex row, so the ask has to
  // claim a full row of its own rather than queue up beside the chips, and its
  // own 8px margins have to go — the strip already insets its content, and two
  // 8px gaps stacked read as a gap twice the size of every other one.
  const isHitl7 = useSelector(selectIsHitl7Enabled);
  // HITL-1534: a suggestion is something to SAY to the customer at the menu
  // board, so once the car has pulled away it is dead by definition — accepting
  // it would speak the departed customer's line (their total) at whoever is at
  // the board next. The slice reset on car_enter handles the next session; this
  // covers the window between car_exit and that car_enter.
  //
  // Deliberately a DISPLAY gate rather than a car_exit reset of the slice:
  // clearing AI state on car_exit caused a prod incident (dropped order + cart
  // revert) because a car can leave the board while the same session's order is
  // still being finished — see the note at CustomerPulse/index.tsx. Hiding the
  // card destroys nothing, so a session that outlives the car keeps its state.
  const isCarPresent = useSelector(selectIsCarPresent);
  const { activeSuggestion, accept, dismiss, getDisplayText } =
    useAISuggestionActions();
  const { flagIssue, isHitl7IssueReportingEnabled } = useIssueReport();
  const showAsk = isAgentCartEnabled && isCarPresent && !!activeSuggestion;

  // Alt+A / Alt+D mirror the Accept / Dismiss buttons (shared path via
  // useAISuggestionActions). `event.code` is matched (not `event.key`) so macOS
  // Option-key glyphs don't break the match; capture phase so it fires even
  // while the omnibar is focused. No-ops unless the co-pilot is enabled and a
  // suggestion is showing.
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyA' && e.code !== 'KeyD') {
      return;
    }
    // Gated on `showAsk`, not just the flag + suggestion: the shortcut must not
    // reach a card the agent cannot see. Without the car-presence check here the
    // hidden post-car_exit suggestion would still be acceptable by keyboard
    // (HITL-1534), which is the harmful half of the bug.
    if (!showAsk || !activeSuggestion) {
      return;
    }
    e.preventDefault();
    // stopPropagation, unlike Alt+1…9 in useIntentHotkeys, which deliberately lets
    // the take-over guard see the press first. Not an inconsistency: the rule is not
    // "speaking to the customer prompts", it is "acting on your OWN initiative
    // prompts". Accepting a co-pilot card ratifies the AI's own line — it goes out
    // stamped `intent: 'ai_suggestion'` and the AI keeps driving — where Alt+1 is an
    // utterance the agent chose, which is a takeover. Prompting here would ask the
    // agent to confirm taking over from the AI in order to agree with it.
    e.stopPropagation();
    if (e.code === 'KeyA') {
      accept(activeSuggestion);
    } else {
      dismiss(activeSuggestion);
    }
  };
  useEventListener('keydown', handleKeyDown, window, true);

  /**
   * HITL-1655 — flag the suggestion, which also DISMISSES it.
   *
   * One decision, one action: an agent flagging a suggestion has already decided not to
   * use it, so leaving the card on screen would make them dismiss it a second time to
   * say the same thing.
   *
   * `agent_suggestion_response` stays honest — every dismissal is still recorded as a
   * dismissal, and some now have a reason attached. That is the point of this surface:
   * today a dismissal records THAT the agent refused a suggestion and never WHY, which
   * is exactly the open question behind low co-pilot acceptance.
   *
   * Order matters. The report is filed FIRST, because `dismiss` clears the suggestion
   * from the slice and the id and text would be gone.
   */
  const flagSuggestion = () => {
    if (!activeSuggestion) return;
    flagIssue({
      source: 'ai_suggestion',
      // Seeded, unlike the cart row: a flag on a suggestion already says what is wrong
      // with it — the AI proposed the wrong line. Still only a seed, so the panel opens
      // with the chip pre-selected and the agent confirms or changes it.
      issue_type: AI_INCORRECT_RESPONSE_ISSUE_TYPE,
      issue_subject: {
        kind: 'ai_suggestion',
        id: activeSuggestion.suggestion_id ?? null,
        // The RESOLVED text, matching what the agent read on the card rather than the
        // raw `{total}` template the AI sent — the report should say what they saw.
        label: getDisplayText(activeSuggestion) || null,
      },
    });
    dismiss(activeSuggestion);
  };

  if (!showAsk || !activeSuggestion) {
    return null;
  }

  const actions = (
    <>
      <IconButton
        className={isHitl7 ? undefined : classes.okBtn}
        size="small"
        title="Accept (Alt+A)"
        aria-label="Accept suggestion"
        onClick={() => accept(activeSuggestion)}
        sx={isHitl7 ? HITL7_ACCEPT_SX : { p: 0.5 }}
      >
        <CheckIcon
          fontSize="small"
          style={isHitl7 ? HITL7_MARK_STYLE : undefined}
          data-testid="pulse-advise-accept-icon"
        />
      </IconButton>
      <IconButton
        className={isHitl7 ? undefined : classes.noBtn}
        size="small"
        title="Dismiss (Alt+D)"
        aria-label="Dismiss suggestion"
        onClick={() => dismiss(activeSuggestion)}
        sx={isHitl7 ? HITL7_DISMISS_SX : { p: 0.5 }}
      >
        <CloseIcon
          fontSize="small"
          style={isHitl7 ? HITL7_MARK_STYLE : undefined}
          data-testid="pulse-advise-dismiss-icon"
        />
      </IconButton>
    </>
  );

  if (isHitl7) {
    return (
      <Box
        // A `Box` rather than a bare `div`, and ONLY because the hover reveal is a
        // descendant selector — `&:hover .issue-flag-badge` — which an inline
        // `style` object cannot express. `HITL7_BOX_STYLE` is spread in unchanged,
        // so the box itself renders exactly as it did.
        sx={{
          ...HITL7_BOX_STYLE,
          ...(isHitl7IssueReportingEnabled
            ? {
                // The mirror of `marginLeft` above, and for the same reason: the
                // transcript's 4px of horizontal padding alone does not clear the
                // 6px the flag badge hangs past this box's right edge, so the
                // corner was being clipped. 4 + 6 = 10, past the 6 it needs.
                //
                // Gated, because it is 6px of real width taken off every
                // suggestion line — worth paying only where the badge exists.
                // Nothing is needed at the TOP: `MessageContainer` already ends
                // in a 10px spacer, which the 6px overhang sits inside.
                marginRight: '6px',
                ...ISSUE_FLAG_BADGE_REVEAL_SX,
              }
            : {}),
        }}
        data-testid="pulse-advise"
        // The transcript files an "ASR MISSING" issue report from a click on
        // its own blank area, and this box now lives INSIDE that click target.
        // Without this marker every Accept would quietly file a spurious
        // Missing Transcription report alongside sending the line. Matched with
        // closest() in LiveTranscriptCard, so the buttons are covered too.
        data-suggestion="true"
      >
        <span style={HITL7_BADGE_STYLE} data-testid="pulse-advise-badge">
          <AutoAwesomeOutlinedIcon style={HITL7_BADGE_ICON_STYLE} />
        </span>
        <span style={HITL7_TEXT_STYLE}>{getDisplayText(activeSuggestion)}</span>
        <div style={HITL7_ACTIONS_STYLE}>{actions}</div>
        {isHitl7IssueReportingEnabled && (
          // The corner badge, NOT a third mark beside Accept and Dismiss.
          //
          // Those two are verdicts on what the suggestion says, and a flag is not a
          // third opinion on the same question — it is a statement about the whole
          // box. Sitting it in their row made it read as one of them; the corner is
          // the same placement the cart rows and the order banner use, and the same
          // one the Issue button's own badge has always had.
          //
          // The box is already `position: relative` for the copilot badge on its
          // left border, and its 6px right padding covers the overhang.
          <IconButton
            className={ISSUE_FLAG_BADGE_CLASS}
            sx={ISSUE_FLAG_BADGE_SX}
            title="Report an issue with this suggestion"
            aria-label="Report an issue with this suggestion"
            data-testid="pulse-advise-flag"
            onClick={flagSuggestion}
          >
            <FlagOutlinedIcon
              style={ISSUE_FLAG_BADGE_ICON_STYLE}
              data-testid="pulse-advise-flag-icon"
            />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <div className={classes.ask} data-testid="pulse-advise">
      {/* HITL-1429: the AI passes the brand menu's own prompt through
          verbatim, so the title can carry `{total}` / `{RESTAURANT_NAME}`
          placeholders. Render it resolved (behind the flag) so the agent reads
          the same line the customer will hear, not raw braces. */}
      <span className={classes.q}>{getDisplayText(activeSuggestion)}</span>
      <div className={classes.actions}>{actions}</div>
    </div>
  );
};

export default PulseAdvise;
